import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Stack,
  Alert,
  useTheme,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  DenominationBreakdownDto,
  UK_NOTE_DENOMINATIONS,
  UK_COIN_DENOMINATIONS,
  formatCashAmount,
} from '../../../app/services/api/cashTransactions';

interface DenominationBreakdownProps {
  value?: DenominationBreakdownDto;
  onChange: (breakdown: DenominationBreakdownDto) => void;
  expectedTotal?: number;
  currencyCode?: string;
  showQuickTotalToggle?: boolean;
  disabled?: boolean;
}

const DenominationBreakdown: React.FC<DenominationBreakdownProps> = ({
  value,
  onChange,
  expectedTotal,
  currencyCode = 'GBP',
  showQuickTotalToggle = true,
  disabled = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('cashReconciliation');

  const [useQuickTotal, setUseQuickTotal] = useState<boolean>(false);
  const [quickTotal, setQuickTotal] = useState<string>('');

  const [notes, setNotes] = useState<{ [key: string]: number }>(
    value?.notes || UK_NOTE_DENOMINATIONS.reduce((acc, denom) => ({ ...acc, [denom]: 0 }), {}),
  );

  const [coins, setCoins] = useState<{ [key: string]: number }>(
    value?.coins || UK_COIN_DENOMINATIONS.reduce((acc, denom) => ({ ...acc, [denom]: 0 }), {}),
  );

  // Calculate totals
  const totalNotes = Object.entries(notes).reduce(
    (sum, [denom, count]) => sum + parseFloat(denom) * count,
    0,
  );

  const totalCoins = Object.entries(coins).reduce(
    (sum, [denom, count]) => sum + parseFloat(denom) * count,
    0,
  );

  const grandTotal = totalNotes + totalCoins;

  const variance = expectedTotal !== undefined ? grandTotal - expectedTotal : 0;
  const hasVariance = Math.abs(variance) > 0.01;

  // Update parent when breakdown changes
  useEffect(() => {
    if (useQuickTotal) {
      // In quick total mode, we only pass the total
      const total = parseFloat(quickTotal) || 0;
      onChange({
        notes: {},
        coins: {},
        totalNotes: 0,
        totalCoins: 0,
        grandTotal: total,
      });
    } else {
      // In breakdown mode, pass full denomination details
      onChange({
        notes: { ...notes },
        coins: { ...coins },
        totalNotes,
        totalCoins,
        grandTotal,
      });
    }
  }, [notes, coins, useQuickTotal, quickTotal]);

  const handleNoteCountChange = (denomination: string, count: string) => {
    const numCount = parseInt(count) || 0;
    setNotes(prev => ({ ...prev, [denomination]: numCount >= 0 ? numCount : 0 }));
  };

  const handleCoinCountChange = (denomination: string, count: string) => {
    const numCount = parseInt(count) || 0;
    setCoins(prev => ({ ...prev, [denomination]: numCount >= 0 ? numCount : 0 }));
  };

  const handleQuickTotalChange = (value: string) => {
    setQuickTotal(value);
  };

  const toggleCountMethod = (checked: boolean) => {
    setUseQuickTotal(checked);
    if (checked) {
      // Switching to quick total - pre-fill with current calculated total
      setQuickTotal(grandTotal.toFixed(2));
    }
  };

  return (
    <Box>
      {showQuickTotalToggle && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography
            variant="textLG"
            fontWeight={600}
          >
            {t('cashCount.denominationBreakdown', 'Cash Count')}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={useQuickTotal}
                onChange={(e) => toggleCountMethod(e.target.checked)}
                disabled={disabled}
              />
            }
            label={t('cashCount.useQuickTotal', 'Quick Total')}
          />
        </Stack>
      )}

      {useQuickTotal ? (
        // Quick Total Mode
        <Box>
          <TextField
            fullWidth
            label={t('cashCount.totalCash', 'Total Cash')}
            type="number"
            value={quickTotal}
            onChange={(e) => handleQuickTotalChange(e.target.value)}
            disabled={disabled}
            inputProps={{
              step: '0.01',
              min: '0',
            }}
            sx={{ mb: 2 }}
          />

          {expectedTotal !== undefined && (
            <Stack spacing={1}>
              <Box
                display="flex"
                justifyContent="space-between"
              >
                <Typography
                  variant="textMD"
                  color="text.secondary"
                >
                  {t('cashCount.expectedTotal', 'Expected Total')}:
                </Typography>
                <Typography
                  variant="textMD"
                  fontWeight={600}
                >
                  {formatCashAmount(expectedTotal, currencyCode)}
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
              >
                <Typography
                  variant="textMD"
                  color="text.secondary"
                >
                  {t('cashCount.declaredTotal', 'Declared Total')}:
                </Typography>
                <Typography
                  variant="textMD"
                  fontWeight={600}
                >
                  {formatCashAmount(parseFloat(quickTotal) || 0, currencyCode)}
                </Typography>
              </Box>
              {hasVariance && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="textMD"
                    fontWeight={600}
                  >
                    {t('cashCount.variance', 'Variance')}:
                  </Typography>
                  <Typography
                    variant="textMD"
                    fontWeight={600}
                    color={variance > 0 ? 'success.main' : 'error.main'}
                  >
                    {variance > 0 ? '+' : ''}
                    {formatCashAmount(variance, currencyCode)}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      ) : (
        // Denomination Breakdown Mode
        <Box>
          <Grid
            container
            spacing={3}
          >
            {/* Notes Section */}
            <Grid
              item
              xs={12}
              md={6}
            >
              <Paper
                variant="outlined"
                sx={{ p: 2 }}
              >
                <Typography
                  variant="textMD"
                  fontWeight={600}
                  mb={2}
                >
                  {t('cashCount.notes', 'Notes')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('cashCount.denomination', 'Denomination')}</TableCell>
                        <TableCell align="center">{t('cashCount.count', 'Count')}</TableCell>
                        <TableCell align="right">{t('cashCount.total', 'Total')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {UK_NOTE_DENOMINATIONS.map((denom) => {
                        const count = notes[denom] || 0;
                        const rowTotal = parseFloat(denom) * count;
                        return (
                          <TableRow key={denom}>
                            <TableCell>
                              <Chip
                                label={`£${denom}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                value={count}
                                onChange={(e) => handleNoteCountChange(denom, e.target.value)}
                                disabled={disabled}
                                size="small"
                                inputProps={{
                                  min: '0',
                                  style: { textAlign: 'center' },
                                }}
                                sx={{ width: '80px' }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="textSM"
                                fontWeight={count > 0 ? 600 : 400}>
                                {formatCashAmount(rowTotal, currencyCode)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="textMD"
                            fontWeight={700}>
                            {t('cashCount.subtotal', 'Subtotal')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="textMD"
                            fontWeight={700}
                            color="primary"
                          >
                            {formatCashAmount(totalNotes, currencyCode)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Coins Section */}
            <Grid
              item
              xs={12}
              md={6}
            >
              <Paper
                variant="outlined"
                sx={{ p: 2 }}
              >
                <Typography
                  variant="textMD"
                  fontWeight={600}
                  mb={2}
                >
                  {t('cashCount.coins', 'Coins')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('cashCount.denomination', 'Denomination')}</TableCell>
                        <TableCell align="center">{t('cashCount.count', 'Count')}</TableCell>
                        <TableCell align="right">{t('cashCount.total', 'Total')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {UK_COIN_DENOMINATIONS.map((denom) => {
                        const count = coins[denom] || 0;
                        const rowTotal = parseFloat(denom) * count;
                        return (
                          <TableRow key={denom}>
                            <TableCell>
                              <Chip
                                label={`£${denom}`}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                value={count}
                                onChange={(e) => handleCoinCountChange(denom, e.target.value)}
                                disabled={disabled}
                                size="small"
                                inputProps={{
                                  min: '0',
                                  style: { textAlign: 'center' },
                                }}
                                sx={{ width: '80px' }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="textSM"
                                fontWeight={count > 0 ? 600 : 400}
                              >
                                {formatCashAmount(rowTotal, currencyCode)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography
                            variant="textMD"
                            fontWeight={700}
                          >
                            {t('cashCount.subtotal', 'Subtotal')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="textMD"
                            fontWeight={700}
                            color="secondary"
                          >
                            {formatCashAmount(totalCoins, currencyCode)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Grand Total and Variance */}
          <Paper
            variant="outlined"
            sx={{
              mt: 3,
              p: 2,
              bgcolor: hasVariance ? theme.palette.error.light + '10' : theme.palette.success.light + '10',
            }}
          >
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <Stack spacing={1}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="textMD"
                      color="text.secondary"
                    >
                      {t('cashCount.notesTotal', 'Notes Total')}:
                    </Typography>
                    <Typography
                      variant="textMD"
                      fontWeight={600}
                    >
                      {formatCashAmount(totalNotes, currencyCode)}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="textMD"
                      color="text.secondary"
                    >
                      {t('cashCount.coinsTotal', 'Coins Total')}:
                    </Typography>
                    <Typography
                      variant="textMD"
                      fontWeight={600}
                    >
                      {formatCashAmount(totalCoins, currencyCode)}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    sx={{ pt: 1, borderTop: `2px solid ${theme.palette.divider}` }}
                  >
                    <Typography
                      variant="textLG"
                      fontWeight={700}
                    >
                      {t('cashCount.grandTotal', 'Grand Total')}:
                    </Typography>
                    <Typography
                      variant="textLG"
                      fontWeight={700}
                      color="primary"
                    >
                      {formatCashAmount(grandTotal, currencyCode)}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {expectedTotal !== undefined && (
                <Grid item
                  xs={12}
                  md={6}>
                  <Stack spacing={1}>
                    <Box display="flex"
                      justifyContent="space-between">
                      <Typography variant="textMD"
                        color="text.secondary">
                        {t('cashCount.expectedTotal', 'Expected Total')}:
                      </Typography>
                      <Typography variant="textMD"
                        fontWeight={600}>
                        {formatCashAmount(expectedTotal, currencyCode)}
                      </Typography>
                    </Box>
                    <Box display="flex"
                      justifyContent="space-between">
                      <Typography variant="textMD"
                        color="text.secondary">
                        {t('cashCount.declaredTotal', 'Declared Total')}:
                      </Typography>
                      <Typography variant="textMD"
                        fontWeight={600}>
                        {formatCashAmount(grandTotal, currencyCode)}
                      </Typography>
                    </Box>
                    <Box display="flex"
                      justifyContent="space-between"
                      sx={{ pt: 1, borderTop: `2px solid ${theme.palette.divider}` }}>
                      <Typography variant="textLG"
                        fontWeight={700}>
                        {t('cashCount.variance', 'Variance')}:
                      </Typography>
                      <Typography
                        variant="textLG"
                        fontWeight={700}
                        color={variance > 0 ? 'success.main' : variance < 0 ? 'error.main' : 'text.primary'}
                      >
                        {variance > 0 ? '+' : ''}
                        {formatCashAmount(variance, currencyCode)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              )}
            </Grid>

            {hasVariance && Math.abs(variance) > 5 && (
              <Alert severity="warning"
                sx={{ mt: 2 }}>
                {t(
                  'cashCount.varianceWarning',
                  'Significant variance detected. Please recount or provide explanation.',
                )}
              </Alert>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default DenominationBreakdown;
